import React from 'react';
import { Link, useParams, useLocation } from 'react-router-dom';
import { ShoppingBag, Sparkles, ArrowRight, Phone, Mail, Gem, Star } from 'lucide-react';
import { usePageTitle } from '../hooks/usePageTitle';

const CRYSTAL_IMAGE = 'https://images.unsplash.com/photo-1643884132093-d995d75a7e67?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTV8MHwxfHNlYXJjaHwxfHxjcnlzdGFsJTIwZ2Vtc3RvbmUlMjBteXN0aWNhbCUyMHB1cnBsZSUyMGdsb3d8ZW58MHx8fHwxNzcxNzIyMjc2fDA&ixlib=rb-4.1.0&q=85';

const BackorderPage = () => {
  const { slug } = useParams();
  const location = useLocation();

  const displayName = slug
    ? slug.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
    : 'Product';

  usePageTitle(`${displayName} - Currently Unavailable`);

  return (
    <div className="min-h-screen bg-[#faf8f5]">
      {/* Hero */}
      <section className="relative py-20 px-6 bg-gradient-to-br from-[#1e1919] via-[#2a2030] to-[#1e1919] overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-10 left-1/4 w-64 h-64 bg-[#9D4EDD]/40 rounded-full blur-3xl" />
          <div className="absolute bottom-0 right-1/4 w-80 h-80 bg-[#FF6B9D]/20 rounded-full blur-3xl" />
        </div>

        <div className="max-w-4xl mx-auto relative z-10 text-center">
          <div className="mb-8">
            <img
              src={CRYSTAL_IMAGE}
              alt={displayName}
              className="w-48 h-48 object-cover rounded-2xl mx-auto shadow-2xl border-2 border-white/10"
              data-testid="backorder-image"
            />
          </div>
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#FFB800]/20 border border-[#FFB800]/30 mb-6">
            <ShoppingBag className="w-4 h-4 text-[#FFB800]" />
            <span className="text-sm text-[#FFB800] font-semibold tracking-wider uppercase" data-testid="backorder-badge">Backorder / Not Available</span>
          </div>

          <h1 className="font-serif text-4xl md:text-5xl text-white mb-4" data-testid="backorder-title">
            {displayName}
          </h1>
          <p className="text-lg text-white/60 max-w-xl mx-auto mb-4">
            This item is currently on backorder or no longer available in our collection.
            Our curated inventory is limited and hand-selected for quality.
          </p>
          <p className="text-white/40 text-sm mb-10">
            Looking for something specific? We offer custom sourcing for rare and specialty gemstones.
          </p>
        </div>
      </section>

      {/* Options */}
      <section className="py-16 px-6 -mt-8">
        <div className="max-w-4xl mx-auto">
          <div className="grid md:grid-cols-2 gap-6">
            {/* Custom Order Card */}
            <div className="bg-white rounded-2xl p-8 shadow-lg border border-[#e8e4de] hover:shadow-xl hover:border-[#9D4EDD]/30 transition-all" data-testid="custom-order-card">
              <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[#9D4EDD] to-[#FF6B9D] flex items-center justify-center mb-6">
                <Gem className="w-7 h-7 text-white" />
              </div>
              <h3 className="font-serif text-2xl text-[#1e1919] mb-3">Order Something Custom</h3>
              <p className="text-[#61525a] mb-6 leading-relaxed">
                Can't find what you're looking for? Our crystal experts can source rare, high-quality
                specimens tailored to your specific needs — whether for healing, collection, or gifting.
              </p>
              <ul className="space-y-2 mb-6 text-sm text-[#4A4A6A]">
                <li className="flex items-center gap-2"><Sparkles className="w-3 h-3 text-[#9D4EDD]" /> Hand-selected to your specifications</li>
                <li className="flex items-center gap-2"><Sparkles className="w-3 h-3 text-[#9D4EDD]" /> Energetically charged upon request</li>
                <li className="flex items-center gap-2"><Sparkles className="w-3 h-3 text-[#9D4EDD]" /> Worldwide sourcing network</li>
              </ul>
              <Link
                to="/education"
                className="inline-flex items-center gap-2 w-full justify-center py-3 bg-gradient-to-r from-[#9D4EDD] to-[#FF6B9D] text-white rounded-xl font-semibold hover:shadow-lg transition-all"
                data-testid="custom-order-btn"
              >
                <Mail className="w-4 h-4" /> Contact Us for Custom Orders
              </Link>
            </div>

            {/* Sacred Six Collection Card */}
            <div className="bg-gradient-to-br from-[#1e1919] to-[#2a2030] rounded-2xl p-8 shadow-lg border border-[#c9a96e]/20 hover:shadow-xl hover:border-[#c9a96e]/50 transition-all" data-testid="sacred-six-card">
              <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[#c9a96e] to-[#FFB800] flex items-center justify-center mb-6">
                <Sparkles className="w-7 h-7 text-[#1e1919]" />
              </div>
              <h3 className="font-serif text-2xl text-white mb-3">The Sacred Six Collection</h3>
              <p className="text-white/60 mb-6 leading-relaxed">
                Discover our exclusive collection of six hand-activated crystals — each personally
                selected, energetically charged, and ready to transform your spiritual practice.
              </p>
              <ul className="space-y-2 mb-6 text-sm text-white/50">
                <li className="flex items-center gap-2"><Star className="w-3 h-3 text-[#c9a96e]" /> Master Healer Clear Quartz</li>
                <li className="flex items-center gap-2"><Star className="w-3 h-3 text-[#c9a96e]" /> Guardian Black Onyx</li>
                <li className="flex items-center gap-2"><Star className="w-3 h-3 text-[#c9a96e]" /> Prosperity Green Aventurine</li>
                <li className="flex items-center gap-2"><Star className="w-3 h-3 text-[#c9a96e]" /> Angelic Selenite Tower & more</li>
              </ul>
              <Link
                to="/collection"
                className="inline-flex items-center gap-2 w-full justify-center py-3 bg-gradient-to-r from-[#c9a96e] to-[#FFB800] text-[#1e1919] rounded-xl font-bold hover:shadow-lg transition-all"
                data-testid="sacred-six-btn"
              >
                View Sacred Six Collection <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Additional Help */}
      <section className="py-12 px-6 bg-gradient-to-r from-[#f8f5ff] to-[#fff5f8]">
        <div className="max-w-2xl mx-auto text-center">
          <Phone className="w-8 h-8 text-[#9D4EDD] mx-auto mb-4" />
          <h3 className="font-serif text-xl text-[#1e1919] mb-2">Need Help Finding Something?</h3>
          <p className="text-[#61525a] mb-4 text-sm">
            Call us directly and our crystal specialists will help you find exactly what you need.
          </p>
          <a href="tel:+14244507018" className="text-[#9D4EDD] font-semibold hover:underline">
            +1 (424) 450-7018
          </a>
        </div>
      </section>
    </div>
  );
};

export default BackorderPage;
